import type {
  Brand,
  Toggler,
} from '@/pages/catalog-detail/ui/Filters/model/types/filters';

import { useCategories } from '@/entities/categories/composables/useCategories';

export const useFilters = async () => {
  const route = useRoute();
  const router = useRouter();
  const { $productsService } = useNuxtApp();
  const { selectedItem, category } = useCategories();
  const { t } = useI18n();

  const brands: Ref<Brand[]> = ref([]);
  const defaultMinPrice = ref(0);
  const defaultMaxPrice = ref(0);
  const minPrice: Ref<number> = ref(0);
  const maxPrice: Ref<number> = ref(0);
  const toggles: Ref<Toggler[]> = ref([
    { id: '1', name: t('promotions'), queryParam: 'action', checked: !!route.query.action },
    { id: '2', name: t('new_arrival'), queryParam: 'new_arrival', checked: !!route.query.new_arrival },
    { id: '3', name: t('sale'), queryParam: 'sale', checked: !!route.query.sale },
    { id: '4', name: t('popular'), queryParam: 'popular', checked: !!route.query.popular },
    { id: '5', name: t('liquidation'), queryParam: 'liquidation', checked: !!route.query.liquidation },
  ]);

  const { data: filtersData } = await useAsyncData('filters', async () => {
    const [brands, prices] = await Promise.all([
      $productsService.getCategoryBrands(Number(category.value?.id)),
      $productsService.getMinMaxPrice(
        selectedItem ? Number(selectedItem.value?.id) : 0
      )
    ]);
    return { brands: brands.data, prices: prices.data };
  }, {
    transform: (data) =>  {
      return {
        brands: data.brands.map((brand: Brand) => ({
          id: brand.id,
          name: brand.name,
          checked: route.query.brand?.includes(brand.id) || false,
        })),
        prices: data.prices,
      };
    },
  });

  brands.value = filtersData.value?.brands || [];
  defaultMinPrice.value = filtersData.value?.prices?.min_price || 0;
  defaultMaxPrice.value = filtersData.value?.prices?.max_price || 0;
  minPrice.value = route.query.min_price ? Number(route.query.min_price) : defaultMinPrice.value;
  maxPrice.value = route.query.max_price ? Number(route.query.max_price) : defaultMaxPrice.value;

  const filtersQuery = computed(() => {
    const query: Record<string, string> = {};

    if (minPrice.value > defaultMinPrice.value) {
      query.min_price = String(minPrice.value);
    }
    if (maxPrice.value < defaultMaxPrice.value) {
      query.max_price = String(maxPrice.value);
    }

    const selectedBrands = brands.value.filter((b: Brand) => b.checked).map((b: Brand) => b.id);
    if (selectedBrands.length > 0) {
      query.brand = selectedBrands.join(',');
    }

    toggles.value.forEach(toggle => {
      if (toggle.checked) {
        query[toggle.queryParam] = String(!!toggle.queryParam);
      }
    });

    return query;
  });

  const applyFilters = (): void => {
    router.push({ query: filtersQuery.value });
    refreshNuxtData('catalog-products');
  };

  const clearFilters = (): void => {
    minPrice.value = defaultMinPrice.value;
    maxPrice.value = defaultMaxPrice.value;

    brands.value.forEach((brand: Brand) => brand.checked = false);
    toggles.value.forEach((toggle: Toggler) => toggle.checked = false);

    router.push({ query: {} });
    refreshNuxtData('catalog-products');
  };

  return {
    minPrice,
    maxPrice,
    defaultMinPrice,
    defaultMaxPrice,
    brands,
    toggles,
    filtersQuery,
    applyFilters,
    clearFilters,
  };
};
